import type { ColumnState, GridApi } from "ag-grid-community";
import { useCallback } from "react";
import { getLocalStorageItem, setLocalStorageItem } from "src/utils/localStorage";

type PersistedColumnState = Pick<ColumnState, "colId" | "flex" | "hide" | "pinned" | "width">;
type PersistedColumnGroupState = { groupId: string; open: boolean };

type PersistedGridLayout = {
  version: 1;
  columns: PersistedColumnState[];
  columnGroups: PersistedColumnGroupState[];
};

const isPersistedColumnState = (value: unknown): value is PersistedColumnState => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Partial<PersistedColumnState>;
  return typeof candidate.colId === "string";
};

const isPersistedColumnGroupState = (value: unknown): value is PersistedColumnGroupState => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Partial<PersistedColumnGroupState>;
  return typeof candidate.groupId === "string" && typeof candidate.open === "boolean";
};

const isPersistedGridLayout = (value: unknown): value is PersistedGridLayout => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Partial<PersistedGridLayout>;
  return (
    candidate.version === 1 &&
    Array.isArray(candidate.columns) &&
    candidate.columns.every(isPersistedColumnState) &&
    Array.isArray(candidate.columnGroups) &&
    candidate.columnGroups.every(isPersistedColumnGroupState)
  );
};

const toPersistedColumnState = (columnState: ColumnState[]): PersistedColumnState[] =>
  columnState.map(({ colId, flex, hide, pinned, width }) => ({
    colId,
    flex,
    hide,
    pinned,
    width,
  }));

export const saveAgGridLayout = <TData>(
  api: GridApi<TData> | null | undefined,
  storageKey: string,
) => {
  if (!api) {
    return;
  }

  try {
    const layout: PersistedGridLayout = {
      version: 1,
      columns: toPersistedColumnState(api.getColumnState()),
      columnGroups: api.getColumnGroupState(),
    };

    setLocalStorageItem(storageKey, JSON.stringify(layout));
  } catch {
    // Ignore storage persistence failures.
  }
};

export const restoreAgGridLayout = <TData>(api: GridApi<TData>, storageKey: string) => {
  try {
    const storedLayout = getLocalStorageItem(storageKey);

    if (!storedLayout) {
      return;
    }

    const parsedLayout = JSON.parse(storedLayout) as unknown;
    if (!isPersistedGridLayout(parsedLayout)) {
      return;
    }

    if (parsedLayout.columns.length > 0) {
      api.applyColumnState({
        state: parsedLayout.columns,
        applyOrder: true,
      });
    }

    if (parsedLayout.columnGroups.length > 0) {
      api.setColumnGroupState(parsedLayout.columnGroups);
    }
  } catch {
    // Ignore malformed JSON and unavailable storage.
  }
};

export const useAgGridLayoutPersistence = <TData>(storageKey: string) => {
  const persistGridLayout = useCallback(
    (api: GridApi<TData> | null | undefined) => {
      saveAgGridLayout(api, storageKey);
    },
    [storageKey],
  );

  const restoreGridLayout = useCallback(
    (api: GridApi<TData>) => {
      restoreAgGridLayout(api, storageKey);
    },
    [storageKey],
  );

  return {
    persistGridLayout,
    restoreGridLayout,
  };
};
